<template>
    <!-- `persistent` is kept on purpose: a stray backdrop click must not throw
         away a selection the user is part-way through building. It does mean
         Vuetify stops handling Esc for us, so Esc is wired back — but from a
         document-level listener, NOT @keydown.esc here. A handler on the dialog
         only fires when focus is already inside it, which is why Esc worked
         only some of the time. See onDocumentKeydown().

         `data-esc-close="off"` stands this dialog down from the app-wide Esc
         handler (plugins/escCloseDialogs.ts), which now does this for every
         other dialog. This one keeps its own because it has a condition the
         generic handler cannot know about: Esc must NOT close mid-upload. -->
    <v-dialog v-model="isOpen" data-esc-close="off" max-width="900" scrollable persistent>
        <v-card>
            <v-card-title class="d-flex align-center pa-4">
                <FolderIcon size="20" stroke-width="1.5" class="mr-2" />
                <span class="text-h6">{{ title }}</span>
                <v-spacer></v-spacer>
                <v-btn variant="text" icon size="small" @click="close">
                    <XIcon size="18" stroke-width="1.5" />
                </v-btn>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Project filter + search. The dropdown jumps straight to a
                 project without scrolling the list; the search box then looks
                 INSIDE it (server-side, every folder depth), falling back to a
                 search across every project the user can see when no project
                 is selected.

                 When `jobId` is set the picker is LOCKED to that one project:
                 the dropdown is gone entirely (there is nothing to choose) and
                 search can never widen past it. -->
            <div class="px-4 pt-4 pb-2">
                <v-row dense>
                    <v-col v-if="!isProjectLocked" cols="12" sm="5">
                        <v-select
                            v-model="projectFilter"
                            :items="projects"
                            item-title="job_name"
                            item-value="id"
                            label="Project"
                            placeholder="All projects"
                            density="compact"
                            variant="outlined"
                            hide-details
                            clearable
                            :loading="isLoading && projects.length === 0"
                            @update:model-value="onProjectFilterChange"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" :sm="isProjectLocked ? 12 : 7">
                        <v-text-field
                            v-model="searchTerm"
                            :label="searchLabel"
                            density="compact"
                            variant="outlined"
                            hide-details
                            clearable
                            :loading="isSearching"
                            @update:model-value="onSearchInput"
                            @click:clear="clearSearch"
                        >
                            <template #prepend-inner>
                                <SearchIcon size="18" stroke-width="1.5" class="text-grey" />
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>
            </div>

            <!-- Breadcrumb: Storage / Project / Folder / Sub-folder …
                 Locked to a project, "Storage" is plain text rather than a link
                 — the project list it would go back to is not a place this
                 picker is allowed to be. -->
            <div v-if="!searchActive" class="px-4 py-2 d-flex align-center flex-wrap" style="gap: 4px">
                <span v-if="isProjectLocked" class="crumb-sep">Storage</span>
                <a v-else class="crumb" @click="goToProjects">Storage</a>
                <template v-if="currentProject">
                    <span class="crumb-sep">/</span>
                    <a class="crumb" @click="goToProjectRoot">{{ currentProject.job_name || 'Project' }}</a>
                </template>
                <template v-for="(folder, index) in folderStack" :key="folder.id">
                    <span class="crumb-sep">/</span>
                    <a class="crumb" @click="goToFolder(index)">{{ folder.name }}</a>
                </template>
            </div>

            <v-divider></v-divider>

            <v-card-text style="min-height: 320px; max-height: 55vh">
                <div v-if="isLoading" class="d-flex justify-center py-10">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>

                <!-- Search results replace the browse view while a term is
                     active. Folder hits navigate; file hits select, exactly
                     like the browse list. -->
                <template v-else-if="searchActive">
                    <div v-if="isSearching" class="d-flex justify-center py-10">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <div
                        v-else-if="searchResults.folders.length === 0 && visibleSearchFiles.length === 0"
                        class="text-center text-grey py-10"
                    >
                        Nothing matches “{{ searchTerm }}”{{ currentProject ? ' in this project' : '' }}.
                    </div>
                    <v-list v-else density="compact">
                        <v-list-item
                            v-for="folder in searchResults.folders"
                            :key="'sf' + folder.id"
                            @click="openSearchFolder(folder)"
                        >
                            <template #prepend>
                                <FolderIcon size="22" stroke-width="1.5" class="mr-3 text-primary" />
                            </template>
                            <v-list-item-title>{{ folder.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ resultLocation(folder) }}</v-list-item-subtitle>
                        </v-list-item>

                        <v-list-item v-for="file in visibleSearchFiles" :key="'sfile' + file.id" @click="toggleFile(file)">
                            <template #prepend>
                                <v-checkbox-btn
                                    :model-value="isSelected(file)"
                                    color="primary"
                                    class="mr-2"
                                    @click.stop="toggleFile(file)"
                                ></v-checkbox-btn>
                                <FileIcon size="20" stroke-width="1.5" class="mr-3 text-grey" />
                            </template>
                            <v-list-item-title>{{ file.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ resultLocation(file) }}</v-list-item-subtitle>
                        </v-list-item>
                    </v-list>
                </template>

                <template v-else>
                    <!-- Level 1: pick a project -->
                    <template v-if="!currentProject">
                        <div v-if="projects.length === 0" class="text-center text-grey py-10">
                            <!-- Locked, an empty project list is not "there are
                                 no projects" — it is this one project being
                                 unreachable, and errorMessage below the file
                                 list is hidden until a project is open. -->
                            {{ isProjectLocked ? (errorMessage || "This project's Storage is not available.") : 'No projects found in Storage.' }}
                        </div>
                        <v-list v-else density="compact">
                            <v-list-item v-for="project in projects" :key="project.id" @click="openProject(project)">
                                <template #prepend>
                                    <FolderIcon size="22" stroke-width="1.5" class="mr-3 text-primary" />
                                </template>
                                <v-list-item-title>{{ project.job_name }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ (project.folders || []).length }} folder(s) · {{ (project.filesjob || []).length }} file(s)
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </template>

                    <!-- Level 2+: folders and files inside a project / folder -->
                    <template v-else>
                        <div v-if="folders.length === 0 && visibleFiles.length === 0" class="text-center text-grey py-10">
                            {{ files.length ? 'No matching files in this folder.' : 'This folder is empty.' }}
                        </div>

                        <v-list density="compact">
                            <v-list-item v-for="folder in folders" :key="'f' + folder.id" @click="openFolder(folder)">
                                <template #prepend>
                                    <FolderIcon size="22" stroke-width="1.5" class="mr-3 text-primary" />
                                </template>
                                <v-list-item-title>{{ folder.name }}</v-list-item-title>
                            </v-list-item>

                            <v-list-item v-for="file in visibleFiles" :key="'file' + file.id" @click="toggleFile(file)">
                                <template #prepend>
                                    <v-checkbox-btn
                                        :model-value="isSelected(file)"
                                        color="primary"
                                        class="mr-2"
                                        @click.stop="toggleFile(file)"
                                    ></v-checkbox-btn>
                                    <FileIcon size="20" stroke-width="1.5" class="mr-3 text-grey" />
                                </template>
                                <v-list-item-title>{{ file.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ (file.extension || '').toUpperCase() }}</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </template>
                </template>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Upload INTO storage, so the user never has to leave this dialog
                 to put a file into Storage first and then pick it. -->
            <div class="px-4 py-3" v-if="currentProject && !searchActive">
                <v-btn v-if="!showUploader" variant="text" size="small" color="primary" @click="showUploader = true">
                    <CloudUploadIcon size="18" stroke-width="1.5" class="mr-1" />
                    Upload to this folder
                </v-btn>
                <template v-else>
                    <div class="d-flex align-center mb-2">
                        <span class="text-body-2">Uploading to <strong>{{ uploadTargetName }}</strong></span>
                        <v-spacer></v-spacer>
                        <v-btn variant="text" size="small" @click="cancelUpload">Cancel</v-btn>
                    </div>
                    <Vue3Dropzone
                        multiple
                        :maxFiles="Infinity"
                        v-model="uploadFiles"
                        :maxFileSize="uploadConfigStore.maxMb"
                    />
                    <v-progress-linear
                        v-if="isUploading"
                        :model-value="uploadProgress"
                        color="primary"
                        height="8"
                        rounded
                        class="mt-2"
                    ></v-progress-linear>
                    <v-btn
                        class="mt-3"
                        color="primary"
                        size="small"
                        :loading="isUploading"
                        :disabled="uploadFiles.length === 0"
                        @click="uploadToStorage"
                    >
                        Upload
                    </v-btn>
                </template>

                <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">{{ errorMessage }}</v-alert>
            </div>

            <v-divider v-if="currentProject && !searchActive"></v-divider>

            <v-card-actions class="pa-4">
                <span class="text-body-2 text-grey">{{ selected.length }} file(s) selected</span>
                <v-spacer></v-spacer>
                <v-btn variant="text" @click="close">Cancel</v-btn>
                <v-btn color="primary" variant="flat" :disabled="selected.length === 0" @click="confirm">
                    Attach selected
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import Vue3Dropzone from '@jaxtheprime/vue3-dropzone';
import { FolderIcon, FileIcon, CloudUploadIcon, XIcon, SearchIcon } from 'vue-tabler-icons';
import { useUploadConfigStore } from '@/stores/uploadConfig';
import { uploadFilesChunked } from '@/services/chunkedUploadService';

/**
 * Central Storage (File Manager) file picker.
 *
 * Browses the same Project → Folder → File tree the Storage page shows, lets
 * the user multi-select files, and can upload NEW files straight into the
 * folder they are browsing — so "from Storage" never dead-ends when the file
 * isn't in Storage yet.
 *
 * It only SELECTS. Turning a selection into an attachment is the caller's job
 * (each module attaches to its own tables), so this component stays reusable
 * beyond estimates.
 */
export default {
    name: 'StorageFilePicker',
    components: { Vue3Dropzone, FolderIcon, FileIcon, CloudUploadIcon, XIcon, SearchIcon },
    props: {
        modelValue: { type: Boolean, default: false },
        title: { type: String, default: 'Select from Storage' },
        /**
         * Restrict what can be picked, e.g. ['pdf'] for Plans & Takeoff.
         * Empty = everything. Folders are never filtered — you still need to
         * walk through them to reach the files.
         */
        extensions: { type: Array, default: () => [] },
        /**
         * Lock the picker to a single project (job_information.id). Set it and
         * the dialog opens straight inside that project's Storage: no project
         * list, no project dropdown, and search scoped to it. Leave it null for
         * the original browse-everything behaviour.
         *
         * The scoping is server-side — getfolderjobs is asked for this one job
         * (`item_id`) rather than every job being fetched and filtered here —
         * so the other projects' folder and file names never reach the browser.
         */
        jobId: { type: [Number, String], default: null }
    },
    emits: ['update:modelValue', 'select'],
    data() {
        return {
            uploadConfigStore: useUploadConfigStore(),
            isLoading: false,
            projects: [],
            currentProject: null,
            // Breadcrumb trail of folders below the project root. Empty means
            // we're at the project root (folders + job-level files).
            folderStack: [],
            folders: [],
            files: [],
            selected: [],
            // Project dropdown + in-project search.
            projectFilter: null,
            searchTerm: '',
            searchActive: false,
            isSearching: false,
            searchResults: { files: [], folders: [] },
            searchDebounce: null,
            showUploader: false,
            uploadFiles: [],
            isUploading: false,
            uploadProgress: 0,
            errorMessage: ''
        };
    },
    computed: {
        isOpen: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            }
        },
        // A jobId of 0 is not a real job, and '' / null / undefined all mean
        // "not locked", so test for a usable id rather than truthiness alone.
        isProjectLocked() {
            return this.jobId !== null && this.jobId !== undefined && this.jobId !== '' && Number(this.jobId) > 0;
        },
        currentFolder() {
            return this.folderStack.length ? this.folderStack[this.folderStack.length - 1] : null;
        },
        uploadTargetName() {
            return this.currentFolder ? this.currentFolder.name : this.currentProject?.job_name || 'project root';
        },
        // File-type filter (`extensions` prop). Folders are never filtered —
        // they're the path to the files.
        allowedExtensions() {
            return (this.extensions || []).map((e) => String(e).toLowerCase().replace(/^\./, ''));
        },
        visibleFiles() {
            return this.filterByExtension(this.files);
        },
        visibleSearchFiles() {
            return this.filterByExtension(this.searchResults.files);
        },
        searchLabel() {
            return this.currentProject
                ? `Search in ${this.currentProject.job_name || 'this project'}`
                : 'Search all projects';
        }
    },
    watch: {
        modelValue(open) {
            if (open) {
                this.reset();
                this.loadProjects();
                // Bound to the document rather than the dialog: the dialog only
                // receives keydown while focus is inside it, and focus is NOT
                // reliably there — during the initial loading spinner nothing
                // focusable is rendered yet, and clicking the folder list or
                // the backdrop leaves focus on <body>. That is exactly the
                // "sometimes it works" behaviour.
                document.addEventListener('keydown', this.onDocumentKeydown);
            } else {
                document.removeEventListener('keydown', this.onDocumentKeydown);
            }
        }
    },
    mounted() {
        this.uploadConfigStore.ensureLoaded();
    },
    beforeUnmount() {
        if (this.searchDebounce) {
            clearTimeout(this.searchDebounce);
        }
        // The dialog can be torn down while still open (parent route change),
        // which would otherwise leave a listener bound to a dead component.
        document.removeEventListener('keydown', this.onDocumentKeydown);
    },
    methods: {
        reset() {
            this.currentProject = null;
            this.folderStack = [];
            this.folders = [];
            this.files = [];
            this.selected = [];
            this.showUploader = false;
            this.uploadFiles = [];
            this.errorMessage = '';
            this.projectFilter = null;
            this.clearSearch();
        },
        close() {
            this.isOpen = false;
        },
        // Esc closes the picker, EXCEPT while an upload is in flight. Tearing
        // the dialog down mid-upload would hide the progress bar and the Cancel
        // control while the request carries on in the background, leaving the
        // user with no idea whether the file landed. They can still cancel the
        // upload explicitly, then press Esc.
        onDocumentKeydown(event) {
            if (event.key !== 'Escape') return;
            if (this.isUploading) return;

            // Let a nested overlay take Esc first. The Project dropdown is a
            // v-menu rendered outside this dialog's DOM, so without this check
            // one Esc would close the whole picker while the user only meant to
            // dismiss the open dropdown.
            if (document.querySelector('.v-menu.v-overlay--active')) return;

            event.stopPropagation();
            this.close();
        },
        confirm() {
            this.$emit('select', this.selected.slice());
            this.isOpen = false;
        },
        filterByExtension(list) {
            if (this.allowedExtensions.length === 0) {
                return list || [];
            }
            return (list || []).filter((f) =>
                this.allowedExtensions.includes(String(f.extension || '').toLowerCase())
            );
        },
        isSelected(file) {
            return this.selected.some((f) => f.id === file.id);
        },
        toggleFile(file) {
            const index = this.selected.findIndex((f) => f.id === file.id);
            if (index === -1) {
                this.selected.push(file);
            } else {
                this.selected.splice(index, 1);
            }
        },
        async loadProjects() {
            this.isLoading = true;
            this.errorMessage = '';
            try {
                // `item_id` narrows getfolderjobs to one job. It is layered ON
                // TOP of that endpoint's role scoping, not instead of it, so a
                // job the user cannot see comes back empty rather than open.
                const payload = this.isProjectLocked ? { item_id: this.jobId } : {};
                const data = await this.$axios.post(`${this.$baseUrlApi}/getfolderjobs`, payload);
                this.projects = Array.isArray(data) ? data : data?.jobs || data?.data || [];

                // Locked: drop the user straight into the project. There is no
                // list to choose from, and the project-list level would render
                // as a single row they must click through for no reason.
                if (this.isProjectLocked) {
                    const project = this.projects.find((p) => String(p.id) === String(this.jobId));
                    if (project) {
                        this.openProject(project);
                    } else {
                        this.projects = [];
                        this.errorMessage = "This project's Storage is not available to you.";
                    }
                }
            } catch (error) {
                this.projects = [];
                this.errorMessage = 'Could not load Storage. Please try again.';
            } finally {
                this.isLoading = false;
            }
        },
        openProject(project) {
            this.currentProject = project;
            this.projectFilter = project?.id ?? null;
            this.folderStack = [];
            this.showUploader = false;
            this.applyProjectRoot();
        },

        // --- Project dropdown ---
        onProjectFilterChange(projectId) {
            if (!projectId) {
                this.goToProjects();
                this.rerunSearchIfActive();
                return;
            }
            const project = this.projects.find((p) => p.id === projectId);
            if (project) {
                this.openProject(project);
            }
            this.rerunSearchIfActive();
        },
        // A live search re-runs against the newly chosen scope rather than
        // leaving stale results from the previous project on screen.
        rerunSearchIfActive() {
            if (this.searchActive) {
                this.runSearch();
            }
        },

        // --- Search (server-side, every folder depth) ---
        onSearchInput() {
            if (this.searchDebounce) {
                clearTimeout(this.searchDebounce);
            }
            const term = (this.searchTerm || '').trim();
            if (!term) {
                this.clearSearch();
                return;
            }
            this.searchActive = true;
            this.isSearching = true;
            this.searchDebounce = setTimeout(() => this.runSearch(), 350);
        },
        async runSearch() {
            const term = (this.searchTerm || '').trim();
            if (!term) {
                this.clearSearch();
                return;
            }
            this.isSearching = true;
            this.errorMessage = '';
            try {
                const data = await this.$axios.post(`${this.$baseUrlApi}/searchstorage`, {
                    q: term,
                    // Scope to the open project when there is one; otherwise the
                    // API searches every project the user can see. Locked, the
                    // fallback to `jobId` matters: currentProject is briefly
                    // null while the project loads, and an unscoped search in
                    // that window would list other projects' files.
                    job_id: this.currentProject?.id ?? (this.isProjectLocked ? this.jobId : null)
                });
                this.searchResults = {
                    files: (data && data.files) || [],
                    folders: (data && data.folders) || []
                };
            } catch (error) {
                this.searchResults = { files: [], folders: [] };
                this.errorMessage = 'Search failed. Please try again.';
            } finally {
                this.isSearching = false;
            }
        },
        clearSearch() {
            if (this.searchDebounce) {
                clearTimeout(this.searchDebounce);
                this.searchDebounce = null;
            }
            this.searchTerm = '';
            this.searchActive = false;
            this.isSearching = false;
            this.searchResults = { files: [], folders: [] };
        },
        // "Folder / Sub-folder" trail for a search hit. The project name is
        // included only when searching across projects — inside one project it
        // would repeat on every row.
        resultLocation(result) {
            const parts = (result.folder_path || []).map((p) => p.name);
            if (!this.currentProject && result.job_name) {
                parts.unshift(result.job_name);
            }
            return parts.length ? parts.join(' / ') : 'Project root';
        },
        // Jump from a folder hit into that folder, rebuilding the breadcrumb
        // from the ancestors the search returned.
        async openSearchFolder(folder) {
            const project = this.projects.find((p) => p.id === folder.job_id);
            if (project) {
                this.currentProject = project;
                this.projectFilter = project.id;
            }
            this.folderStack = [...(folder.folder_path || []), folder];
            this.clearSearch();
            await this.loadFolderContents(folder);
        },
        // Project root is already in the getfolderjobs payload — no second
        // round-trip needed to show it.
        applyProjectRoot() {
            this.folders = this.currentProject?.folders || [];
            this.files = this.currentProject?.filesjob || [];
        },
        async openFolder(folder) {
            this.folderStack.push(folder);
            await this.loadFolderContents(folder);
        },
        async loadFolderContents(folder) {
            this.isLoading = true;
            this.showUploader = false;
            try {
                const data = await this.$axios.post(`${this.$baseUrlApi}/getfoldercontents`, {
                    folder_id: folder.id,
                    folder_no: folder.folder_no
                });
                this.folders = (data && data.folders) || [];
                this.files = (data && data.files) || [];
            } catch (error) {
                this.folders = [];
                this.files = [];
                this.errorMessage = 'Could not open that folder.';
            } finally {
                this.isLoading = false;
            }
        },
        goToProjects() {
            // Locked, "up" stops at the project root — there is no all-projects
            // level to return to.
            if (this.isProjectLocked) {
                this.goToProjectRoot();
                return;
            }
            this.currentProject = null;
            this.projectFilter = null;
            this.folderStack = [];
            this.folders = [];
            this.files = [];
            this.showUploader = false;
            // Selections are kept deliberately: a user can gather files from
            // more than one project in a single trip through the dialog.
        },
        goToProjectRoot() {
            this.folderStack = [];
            this.showUploader = false;
            this.applyProjectRoot();
        },
        async goToFolder(index) {
            const folder = this.folderStack[index];
            this.folderStack = this.folderStack.slice(0, index + 1);
            await this.loadFolderContents(folder);
        },
        cancelUpload() {
            this.showUploader = false;
            this.uploadFiles = [];
        },
        async refreshCurrentLocation() {
            if (this.currentFolder) {
                await this.loadFolderContents(this.currentFolder);
                return;
            }
            // At the project root the folder/file lists come from getfolderjobs,
            // so re-fetch it and re-point currentProject at the fresh row.
            const currentId = this.currentProject?.id;
            await this.loadProjects();
            const refreshed = this.projects.find((p) => p.id === currentId);
            if (refreshed) {
                this.currentProject = refreshed;
                this.applyProjectRoot();
            }
        },
        async uploadToStorage() {
            const filesToUpload = (this.uploadFiles || [])
                .map((entry) => (entry && entry.file ? entry.file : entry))
                .filter((f) => f instanceof File);

            if (filesToUpload.length === 0) {
                return;
            }

            this.isUploading = true;
            this.uploadProgress = 0;
            this.errorMessage = '';

            try {
                const result = await uploadFilesChunked(filesToUpload, {
                    apiUrl: this.$baseUrlApi,
                    authToken: this.$store.getters.isLoggedIn.token,
                    metadata: {
                        job_id: this.currentProject?.id,
                        // targetFolder is a ProjectFolder.id; null puts the file
                        // at the project root, matching the Storage page.
                        targetFolder: this.currentFolder ? this.currentFolder.id : null
                    },
                    onProgress: (percent) => {
                        this.uploadProgress = percent;
                    }
                });

                if (result.success) {
                    this.uploadFiles = [];
                    this.showUploader = false;
                    await this.refreshCurrentLocation();
                } else {
                    this.errorMessage = result.failed?.name
                        ? `Upload failed for "${result.failed.name}": ${result.error || 'Unknown error'}`
                        : `Upload failed: ${result.error || 'Unknown error'}`;
                }
            } catch (error) {
                this.errorMessage = `Upload failed: ${error?.message || 'Unknown error'}`;
            } finally {
                this.isUploading = false;
                this.uploadProgress = 0;
            }
        }
    }
};
</script>

<style scoped>
.crumb {
    cursor: pointer;
    color: rgb(var(--v-theme-primary));
    font-size: 13px;
}
.crumb:hover {
    text-decoration: underline;
}
.crumb-sep {
    color: #98a2b3;
    font-size: 13px;
}
</style>
